import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, Briefcase, User } from "lucide-react";
import { format, addDays, startOfWeek, isToday } from "date-fns";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DayCard = ({ date, dayData, t, currentUserId }) => {
  const shifts = dayData?.shifts || [];
  const timeOff = dayData?.time_off || [];
  const permanent = dayData?.permanent || [];
  const hasContent = shifts.length > 0 || timeOff.length > 0 || permanent.length > 0;
  
  // Parse date without timezone conversion
  const [year, month, day] = date.split('-').map(Number);
  const dateObj = new Date(year, month - 1, day);
  const today = new Date();
  const isTodayDate = dateObj.getFullYear() === today.getFullYear() && 
                      dateObj.getMonth() === today.getMonth() && 
                      dateObj.getDate() === today.getDate();
  
  return (
    <div className={`border rounded-xl p-3 ${isTodayDate ? 'ring-2 ring-primary bg-primary/5' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="font-medium text-sm">{format(dateObj, 'EEE, MMM d')}</p>
        {isTodayDate && <Badge variant="default" className="text-xs">{t("today") || "Today"}</Badge>}
      </div>
      {!hasContent && <p className="text-xs text-muted-foreground">{t("noShifts") || "No shifts"}</p>}
      {hasContent && (
        <div className="space-y-2">
          {/* Permanent schedules (blue) */}
          {permanent.map((perm, idx) => {
            const isCurrentUser = perm.user_id === currentUserId;
            return (
              <div key={`p-${idx}`} className={`text-xs p-2 rounded-r ${isCurrentUser ? 'bg-blue-100 border-l-4 border-blue-600 ring-1 ring-blue-300' : 'bg-blue-50 border-l-4 border-blue-500'}`}>
                <div className="flex items-center gap-1">
                  {isCurrentUser && <User className="w-3 h-3 text-blue-700" />}
                  <p className={`font-medium ${isCurrentUser ? 'text-blue-800' : 'text-blue-700'}`}>{perm.employee_name}{isCurrentUser && ` (${t("you") || "You"})`}</p>
                </div>
                <p className="text-blue-600">{perm.start_time} - {perm.end_time}</p>
              </div>
            );
          })}
          {/* Shifts/adjustments (teal) */}
          {shifts.map((shift, idx) => {
            const isCurrentUser = shift.user_id === currentUserId;
            return (
              <div key={`s-${idx}`} className={`text-xs p-2 rounded-r ${isCurrentUser ? 'bg-teal-100 border-l-4 border-teal-600 ring-1 ring-teal-300' : 'bg-teal-50 border-l-4 border-teal-500'}`}>
                <div className="flex items-center gap-1">
                  {isCurrentUser && <User className="w-3 h-3 text-teal-700" />}
                  <p className={`font-medium ${isCurrentUser ? 'text-teal-800' : 'text-teal-700'}`}>{shift.employee_name}{isCurrentUser && ` (${t("you") || "You"})`}</p>
                </div>
                <p className="text-teal-600">{shift.start_time} - {shift.end_time}</p>
              </div>
            );
          })}
          {/* Time off (purple) */}
          {timeOff.map((off, idx) => {
            const isCurrentUser = off.user_id === currentUserId;
            return (
              <div key={`t-${idx}`} className={`text-xs p-2 rounded-r ${isCurrentUser ? 'bg-purple-100 border-l-4 border-purple-600 ring-1 ring-purple-300' : 'bg-purple-50 border-l-4 border-purple-500'}`}>
                <div className="flex items-center gap-1">
                  {isCurrentUser && <User className="w-3 h-3 text-purple-700" />}
                  <p className={`font-medium ${isCurrentUser ? 'text-purple-800' : 'text-purple-700'}`}>{off.employee_name}{isCurrentUser && ` (${t("you") || "You"})`}</p>
                </div>
                <p className="text-purple-600">{t("timeOff") || "Off"}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const EmployeeGroupSchedule = () => {
  const { t } = useLanguage();
  const [weekSchedule, setWeekSchedule] = useState(null);
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [currentUserId, setCurrentUserId] = useState(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setCurrentUserId(data.user_id);
        }
      } catch (e) { console.error(e); }
    };
    fetchUser();
  }, []);

  const fetchWeekSchedule = useCallback(async () => {
    try {
      const dateStr = format(weekStart, 'yyyy-MM-dd');
      const res = await fetch(`${API_URL}/api/schedule/week?week_start=${dateStr}`, { credentials: 'include' });
      if (res.ok) setWeekSchedule(await res.json());
    } catch (e) { console.error(e); }
  }, [weekStart]);

  useEffect(() => { fetchWeekSchedule(); }, [fetchWeekSchedule]);

  const goToPrevWeek = () => setWeekStart(addDays(weekStart, -7));
  const goToNextWeek = () => setWeekStart(addDays(weekStart, 7));
  const weekEndDate = addDays(weekStart, 6);
  const weekLabel = `${format(weekStart, 'MMM d')} - ${format(weekEndDate, 'MMM d, yyyy')}`;
  const payType = weekSchedule?.pay_type || "hourly";
  
  // Get schedule dates as array
  const scheduleDates = weekSchedule ? Object.keys(weekSchedule.schedule) : [];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>{t("weeklySchedule") || "Weekly Schedule"}</CardTitle>
            {payType === "salary" && (
              <CardDescription className="flex items-center gap-1 mt-1">
                <Briefcase className="w-3 h-3" />
                {t("salaryScheduleNote") || "Showing permanent schedules. Adjustments and time-off override."}
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goToPrevWeek}><ChevronLeft className="w-4 h-4" /></Button>
            <span className="text-sm font-medium">{weekLabel}</span>
            <Button variant="outline" size="sm" onClick={goToNextWeek}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {weekSchedule ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
              {scheduleDates.map((date) => (
                <DayCard key={date} date={date} dayData={weekSchedule.schedule[date]} t={t} currentUserId={currentUserId} />
              ))}
            </div>
            {/* Legend */}
            <div className="mt-4 flex flex-wrap gap-4 text-xs">
              {payType === "salary" && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-100 border-l-2 border-blue-500 rounded-r"></div>
                  <span>{t("permanentSchedule") || "Permanent"}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-teal-100 border-l-2 border-teal-500 rounded-r"></div>
                <span>{t("shiftAdjustment") || "Adjustment"}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-purple-100 border-l-2 border-purple-500 rounded-r"></div>
                <span>{t("timeOff") || "Time Off"}</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-3 h-3" />
                <span>{t("yourShifts") || "Your shifts are highlighted"}</span>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center text-muted-foreground py-8">{t("loading") || "Loading..."}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeGroupSchedule;
